import { createPortal } from 'react-dom';
import { EditorContent } from '@tiptap/react';
import CloseIcon from '@mui/icons-material/Close';
import JxFormResizableBase from '../../JxControls/JxForms/JxFormResizableBase';
import JxTitleBar from '../../JxControls/JxTitleBar/JxTitleBar';
import JxIconButton from '../../JxControls/JxButtons/JxIconButton';
import JxButton from '../../JxControls/JxButtons/JxButton';
import { jxScrollbarThin } from '../../JxControls/jxScrollbarStyle';
import BodyEditorToolbar from './BodyEditorToolbar';
import ImageContextMenu from './ImageContextMenu';
import { createEditor } from '../Core/EditorHelper';
import styleCache from '../Core/StyleCache';
import userSettingsEngine from '../../controllers/UserSettings/UserSettingsEngine';
import '../../JxControls/JxTheme.css';
import '../../theme/JernyBodyContent.css';
import './BodyEditorDlg.css';

import bodyEditorEngine from '../Core/BodyEditorEngine';

/**
 * BodyEditorDlg — floating WYSIWYG body editor.
 * Extends JxFormResizableBase: drag + resize (all 4 corners + all 4 edges) + bringToTop + Escape to close.
 * No Joi validation — pure rich text, no form fields.
 *
 * Injects the user's active style note CSS as a <style> tag in the portal so the editor
 * always renders with the correct theme, regardless of which note is selected in the list.
 *
 * @param {boolean} props.open
 * @param {object} props.entry - journal entry to edit
 * @param {function} props.onClose
 */
export default class BodyEditorDlg extends JxFormResizableBase {
    constructor(props) {
        super(props);
        this.name = 'BodyEditorDlg';
        this.schema = null; // no Joi validation — pure rich text, no form fields
        this._editor = null;

        // Merge on top of base class state (pos, size, zIndex, dragging, resizing).
        // Do NOT replace this.state outright — panelStyle reads this.state.pos/size.
        this.state = {
            ...this.state,
            imgMenuOpen: false,
            imgMenuPos: { x: 0, y: 0 },
            imgNodePos: null, // ProseMirror position of the clicked image node
            imgNaturalWidth: 0, // natural pixel width captured at right-click time
            imgCurrentWidth: null, // stored width string ("400px" / "50%" / null)
        };
    }

    // Literal, hardcoded default — no window.innerWidth centering math. Adjust these 4
    // numbers directly if the default position/size should change.
    getDefaultGeo() {
        return { top: 120, left: 610, width: 700, height: 560 };
    }

    componentDidMount() {
        super.componentDidMount();
        window.addEventListener('keydown', this._handleKeyDown);
        this._editor = createEditor(this.props.entry?.body, () => this.forceUpdate());
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        if (!this._editor) return;

        // Bring to top whenever the dialog opens — ensures it's above other panels on first render
        if (this.props.open && !prevProps.open) {
            this.bringToTop();
        }

        const newBody = this.props.entry?.body ?? '';
        const prevBody = prevProps.entry?.body ?? '';

        if (this.props.entry?.rk !== prevProps.entry?.rk) {
            this._editor.commands.setContent(newBody);
            return;
        }

        // Guard: skip if editor already has this content.
        // setContent triggers onTransaction → forceUpdate → componentDidUpdate — without this
        // guard that becomes an infinite loop on first open (prevProps.entry is undefined).
        if (newBody !== prevBody && newBody !== this._editor.getHTML()) {
            this._editor.commands.setContent(newBody);
        }
    }

    componentWillUnmount() {
        if (this._editor) {
            this._editor.destroy();
            this._editor = null;
        }
        window.removeEventListener('keydown', this._handleKeyDown);
        super.componentWillUnmount();
    }

    _handleKeyDown = (e) => {
        if (!this.props.open) return;

        this.logDebug('handleKeyDown', 'test');
        if (e.ctrlKey && e.key === 's') {
            e.preventDefault();
            this._handleSave(false);
        }
    };

    /**
     * Intercepts right-click inside editor — opens ImageContextMenu when clicking on an img.
     * Resolves and stores the ProseMirror node position at open time so it stays valid
     * across Tiptap re-renders triggered by subsequent resize operations.
     * Also captures the current stored width so ImageContextMenu can initialize its slider correctly.
     * @param {React.MouseEvent} e
     */
    _handleEditorContextMenu = (e) => {
        if (e.target.tagName !== 'IMG') return;
        e.preventDefault();
        e.stopPropagation();

        const img = e.target;
        let nodePos = null;
        try {
            nodePos = this._editor.view.posAtDOM(img, 0);
        } catch {
            return; // can't resolve position — bail
        }

        // Read stored width from the rendered style (set by ImageWithWidth.renderHTML via inline style).
        const currentWidth = img.style.width || null;

        this.setState({
            imgMenuOpen: true,
            imgMenuPos: { x: e.clientX, y: e.clientY },
            imgNodePos: nodePos,
            imgNaturalWidth: img.naturalWidth,
            imgCurrentWidth: currentWidth,
        });
    };

    _closeImgMenu = () => {
        this.setState({ imgMenuOpen: false, imgNodePos: null, imgCurrentWidth: null });
    };

    handleClose = (action) => {
        if (action === 0) {
            this._handleSave(true);
        } else {
            this.props.onClose();
        }
    };

    _handleSave = async (doClose) => {
        if (!this._editor || !this.props.entry) return;

        const html = this._editor.getHTML();
        await bodyEditorEngine.save(html);

        if (!doClose) return;
        this.props.onClose();
    };

    render() {
        if (!this.props.open) return null;

        const title = this.props.entry?.title ? `Editing: ${this.props.entry.title}` : 'Body Editor';
        const { imgMenuOpen, imgMenuPos, imgNodePos, imgNaturalWidth, imgCurrentWidth } = this.state;

        // Inject active style note CSS — resolved once per render, stable as long as user setting
        // doesn't change. Keeps editor styling self-contained, independent of which note is selected.
        const styleTitle = userSettingsEngine.resolve('bodyEditor.styleNote');
        const css = styleCache.getByTitle(styleTitle)?.body ?? '';

        const panel = (
            <>
                {css && <style>{css}</style>}
                <div {...this.panelProps} style={this.panelStyle} className="be-panel" onKeyDown={(e) => e.stopPropagation()}>
                    <JxTitleBar
                        id={`${this.name}_titlebar`}
                        title={title}
                        variant="toolwindow"
                        isDraggable={true}
                        dragHandleProps={this.dragHandleProps}
                        actions={<JxIconButton icon={<CloseIcon sx={{ fontSize: 16 }} />} title="Close" onClick={this.props.onClose} />}
                    />

                    <BodyEditorToolbar editor={this._editor} />

                    <div className={`be-editor-wrap jerny-body-content ${jxScrollbarThin}`} onContextMenu={this._handleEditorContextMenu}>
                        <EditorContent editor={this._editor} />
                    </div>

                    <div className="be-footer">
                        <JxButton label="Cancel" className="jx-btn-cancel" onClick={() => this.handleClose(-1)} />
                        <JxButton label="Save" className="jx-btn-save dirty" onClick={() => this.handleClose(0)} />
                    </div>

                    {this.renderResizeHandles()}

                    <ImageContextMenu
                        editor={this._editor}
                        pos={imgMenuPos}
                        imgNodePos={imgNodePos}
                        imgNaturalWidth={imgNaturalWidth}
                        imgCurrentWidth={imgCurrentWidth}
                        open={imgMenuOpen}
                        onClose={this._closeImgMenu}
                    />
                </div>
            </>
        );

        return createPortal(panel, document.body);
    }
}
